import React, { useState } from 'react'
import { SMBeamModel, SMBeamLoad } from '@structura/beam-analysis'
import { beamApiBase } from '@structura/services'
import { useTranslation } from '@structura/i18n'

interface TestResult {
  id: string
  name: string
  status: 'pending' | 'running' | 'success' | 'failed'
  logs: string[]
  analysisId?: string
}

// --- Transformers ---

function buildCrossSection(model: SMBeamModel)
{
  return {
    name: model.crossSection.name,
    type: model.crossSection.sectionType || 'I-beam',
    height: model.crossSection.height,
    width: model.crossSection.width,
    webThickness: model.crossSection.webThickness || 0,
    flangeThickness: model.crossSection.flangeThickness || 0,
    area: model.crossSection.area,
    inertiaY: model.crossSection.inertia,
    inertiaZ: model.crossSection.inertia * 0.1, // Simplified
    sectionClass: model.crossSection.sectionClass || 'auto',
    elasticModulusY: model.crossSection.elasticModulusY,
    plasticModulusY: model.crossSection.plasticModulusY,
    elasticModulusZ: model.crossSection.elasticModulusZ,
    plasticModulusZ: model.crossSection.plasticModulusZ,
    reinforcementRatio: model.crossSection.reinforcementRatio,
    effectiveCover: model.crossSection.effectiveCover,
    reinforcementAreaBottom: model.crossSection.reinforcementAreaBottom,
    reinforcementLeverArmBottom: model.crossSection.reinforcementLeverArmBottom,
    effectiveDepth: model.crossSection.effectiveDepth,
    elasticMomentCapacityKNm: model.crossSection.elasticMomentCapacityKNm,
    plasticMomentCapacityKNm: model.crossSection.plasticMomentCapacityKNm,
    reinforcedMomentCapacityKNm: model.crossSection.reinforcedMomentCapacityKNm,
    shearCorrectionFactor: model.crossSection.shearCorrectionFactor
  }
}

function buildLoadCasesPayload(model: SMBeamModel)
{
  return model.loadCases.map((loadCase, index) => {
    const loadEntries = loadCase.loads.map((load) => ({
      id: load.id,
      clientId: load.id,
      spanId: load.spanId,
      loadType: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
      pointLoad: load.pointLoad,
      pointLoadPosition: load.pointLoadPosition,
      normalForce: load.normalForce,
      normalForcePosition: load.normalForcePosition,
      normalForceEccentricity: load.normalForceEccentricity,
      uniformIntensity: load.uniformIntensity,
      startIntensity: load.startIntensity,
      endIntensity: load.endIntensity,
      startPosition: load.startPosition,
      endPosition: load.endPosition,
      axialForceEnd: load.axialForceEnd
    }))

    return {
      id: loadCase.id,
      clientId: loadCase.id,
      name: loadCase.name,
      category: loadCase.type,
      isActive: loadCase.isActive,
      displayOrder: index,
      loads: loadEntries
    }
  })
}

const RegressionTestingPanel: React.FC = () => {
  const { t } = useTranslation()
  const [testResults, setTestResults] = useState<TestResult[]>([
    { id: 'steel_buckling', name: t('admin.regression.testSteel'), status: 'pending', logs: [] },
    { id: 'concrete_dynamics', name: t('admin.regression.testConcrete'), status: 'pending', logs: [] },
    { id: 'timber_vibration', name: t('admin.regression.testTimber'), status: 'pending', logs: [] }
  ])

  const [isRunning, setIsRunning] = useState(false)

  const log = (testId: string, message: string) => {
    setTestResults(prev => prev.map(t => 
      t.id === testId ? { ...t, logs: [...t.logs, message] } : t
    ))
  }

  const setStatus = (testId: string, status: TestResult['status']) => {
    setTestResults(prev => prev.map(t => 
      t.id === testId ? { ...t, status } : t
    ))
  }

  const createBaseModel = (material: string): SMBeamModel => {
    return {
      nodes: [
        { id: 1, position: 0, supportType: 'Pinned', axialFixed: true },
        { id: 2, position: 6, supportType: 'Roller', axialFixed: false }
      ],
      spans: [
        { id: 1, leftNodeId: 1, rightNodeId: 2, length: 6, E: 210e9, A: 0.005, I: 0.0001, loads: [] }
      ],
      loadCases: [],
      crossSection: {
        name: 'Default',
        width: 0,
        height: 0,
        area: 0,
        inertia: 0,
        isCustom: true,
        material: material
      }
    }
  }

  const runTest = async (test: TestResult, model: SMBeamModel, materialParams: any) => {
    setStatus(test.id, 'running')
    log(test.id, `Starting test: ${test.name}`)

    try {
      // Prepare payload using transformers
      const crossSection = buildCrossSection(model)
      const loadCases = buildLoadCasesPayload(model)
      const spans = model.spans.map(s => ({
        id: s.id,
        leftNodeId: s.leftNodeId,
        rightNodeId: s.rightNodeId,
        length: s.length,
        e: s.E,
        a: s.A,
        i: s.I,
        loads: [] // Loads are in LoadCases for multi-span/advanced analysis
      }))
      const nodes = model.nodes.map(n => ({
        id: n.id,
        supportType: n.supportType,
        axialFixed: n.axialFixed,
        position: n.position
      }))

      const analysisPayload = {
        nodes,
        spans,
        crossSection,
        material: materialParams,
        loadCases,
        loadCombinations: [], 
        codeRequirements: {}
      }

      // 1. Analyze
      log(test.id, 'Running Analysis...')
      const analysisRes = await fetch(`${beamApiBase(materialParams.materialType)}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisPayload)
      })

      if (!analysisRes.ok) throw new Error(`Analysis failed: ${analysisRes.statusText}`)
      const analysisData = await analysisRes.json()
      if (!analysisData.success) throw new Error(`Analysis error: ${analysisData.message || analysisData.error || 'Unknown error'}`)
      
      const analysisId = analysisData.analysisId || analysisData.AnalysisId
      log(test.id, `Analysis success. ID: ${analysisId}`)

      // 2. Response (Code Check)
      // We ONLY send AnalysisId to force the backend to fetch Material, CrossSection, etc. from AnalysisSessions.db
      log(test.id, 'Calculating Response (Code Check) via DB...')
      const responseRes = await fetch('/api/Response/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysisId
        })
      })

      if (!responseRes.ok) {
        const errText = await responseRes.text()
        throw new Error(`Response calc failed: ${responseRes.statusText} - ${errText}`)
      }
      const responseData = await responseRes.json()
      if (!responseData.success) throw new Error(`Response error: ${responseData.message}`)
      
      const maxUR = responseData.data?.maxUtilizationRatio || responseData.Data?.maxUtilizationRatio
      log(test.id, `Response success (DB-hydrated). Max UR: ${(maxUR * 100)?.toFixed(1)}%`)

      // 3. Buckling (Second Order)
      if (materialParams.materialType !== 'concrete') { // Using camelCase/exact match from payload
        log(test.id, 'Calculating Buckling via DB...')
        const bucklingRes = await fetch('/api/SecondOrderEffects/calculate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            analysisId,
            combinationId: 'envelope'
            })
        })

        if (!bucklingRes.ok) {
            const errText = await bucklingRes.text()
            throw new Error(`Buckling failed: ${bucklingRes.statusText} - ${errText}`)
        }
        const bucklingData = await bucklingRes.json()
        if (!bucklingData.success) throw new Error(`Buckling error: ${bucklingData.message || bucklingData.error}`)
        const criticalMode = bucklingData.overallResults?.criticalMode || bucklingData.OverallResults?.criticalMode
        log(test.id, `Buckling success (DB-hydrated). Critical Mode: ${criticalMode ?? 'N/A'}`)
      }

      // 4. Dynamics
      log(test.id, 'Calculating Dynamics via DB...')
      const dynamicsRes = await fetch('/api/Dynamics/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysisId,
          analysisType: 'NaturalFrequencies',
          buildingUse: 'Residential',
          numberOfModes: 5
        })
      })

      if (!dynamicsRes.ok) throw new Error(`Dynamics failed: ${dynamicsRes.statusText}`)
      const dynamicsData = await dynamicsRes.json()
      if (!dynamicsData.success) throw new Error(`Dynamics error: ${dynamicsData.message || dynamicsData.error}`)
      const f1 = dynamicsData.results?.fundamentalFrequency || dynamicsData.Results?.fundamentalFrequency
      log(test.id, `Dynamics success. f1: ${f1?.toFixed(2)} Hz`)

      setStatus(test.id, 'success')
      log(test.id, 'Test completed successfully.')

    } catch (err: any) {
      console.error(err)
      log(test.id, `ERROR: ${err.message}`)
      setStatus(test.id, 'failed')
    }
  }

  const runAllTests = async () => {
    setIsRunning(true)
    
    // --- Steel Test Case ---
    const steelModel = createBaseModel('steel')
    // Use IPE 300 template logic (simplified)
    steelModel.crossSection = {
        name: 'IPE 300',
        width: 150,
        height: 300,
        area: 0.00538,
        inertia: 0.0000836, // Iy
        inertiaZ: 0.00000604,
        isCustom: false,
        material: 'steel',
        sectionType: 'I-beam',
        webThickness: 7.1,
        flangeThickness: 10.7,
        sectionClass: '1'
    }
    // Update span properties
    steelModel.spans[0].A = steelModel.crossSection.area
    steelModel.spans[0].I = steelModel.crossSection.inertia
    
    // Add Loads: Uniform + Axial Compression (Using GUIDs for IDs)
    const steelLoads: SMBeamLoad[] = [
        { id: '11111111-1111-1111-1111-111111111112', type: 'UniformLoad', spanId: 1, startPosition: 0, endPosition: 6, uniformIntensity: 10, startIntensity: 10, endIntensity: 10, pointLoad: 0, pointLoadPosition: 0, normalForce: 0, normalForcePosition: 0, normalForceEccentricity: 0 },
        { id: '11111111-1111-1111-1111-111111111113', type: 'AxialForce', spanId: 1, startPosition: 0, endPosition: 0, uniformIntensity: 0, startIntensity: 0, endIntensity: 0, pointLoad: 0, pointLoadPosition: 0, normalForce: 500, normalForcePosition: 0, normalForceEccentricity: 0, axialForceEnd: 'left' } // 500kN compression
    ]
    steelModel.loadCases = [
        { id: '11111111-1111-1111-1111-111111111111', name: 'Test Load', type: 'Variable', isActive: true, loads: steelLoads }
    ]

    // Mock resolved material payload - CAMEL CASE keys
    const steelMaterial = {
        nationalAnnex: 'Standard',
        materialType: 'steel',
        elasticModulus: 210e9,
        density: 7850,
        steelGrade: 'S355',
        steelYieldStrength: 355e6,
        steelUltimateStrength: 510e6,
        steelThickness: undefined
    }

    await runTest(testResults[0], steelModel, steelMaterial)


    // --- Concrete Test Case ---
    const concreteModel = createBaseModel('concrete')
    concreteModel.crossSection = {
        name: 'Rect 300x500',
        width: 300,
        height: 500,
        area: 0.15,
        inertia: (0.3 * 0.5**3) / 12,
        isCustom: true,
        material: 'concrete',
        sectionType: 'rectangular'
    }
     // Update span properties
     concreteModel.spans[0].A = concreteModel.crossSection.area
     concreteModel.spans[0].I = concreteModel.crossSection.inertia
    
    const concreteLoads: SMBeamLoad[] = [
        { id: '22222222-2222-2222-2222-222222222223', type: 'UniformLoad', spanId: 1, startPosition: 0, endPosition: 6, uniformIntensity: 25, startIntensity: 25, endIntensity: 25, pointLoad: 0, pointLoadPosition: 0, normalForce: 0, normalForcePosition: 0, normalForceEccentricity: 0 }
    ]
    concreteModel.loadCases = [
        { id: '22222222-2222-2222-2222-222222222222', name: 'Concrete Load', type: 'Permanent', isActive: true, loads: concreteLoads }
    ]

    const concreteMaterial = {
        nationalAnnex: 'Standard',
        materialType: 'concrete',
        elasticModulus: 33e9,
        density: 2400,
        concreteClass: 'C30/37',
        concreteFck: 30e6,
        alphaCC: 1.0,
        reinforcementClass: 'B500B',
        reinforcementFy: 500e6,
        prestressClass: 'Y1860',
        prestressFpk: 1860e6
    }
    
    await runTest(testResults[1], concreteModel, concreteMaterial)


    // --- Timber Test Case ---
    const timberModel = createBaseModel('timber')
    // 2 Spans for Timber to test multi-span dynamics
    timberModel.nodes = [
        { id: 1, position: 0, supportType: 'Pinned', axialFixed: true },
        { id: 2, position: 4, supportType: 'Roller', axialFixed: false },
        { id: 3, position: 8, supportType: 'Roller', axialFixed: false }
    ]
    timberModel.spans = [
        { id: 1, leftNodeId: 1, rightNodeId: 2, length: 4, E: 13e9, A: 0.08, I: 0.001067, loads: [] },
        { id: 2, leftNodeId: 2, rightNodeId: 3, length: 4, E: 13e9, A: 0.08, I: 0.001067, loads: [] }
    ]

    timberModel.crossSection = {
        name: 'Glulam 200x400',
        width: 200,
        height: 400,
        area: 0.08,
        inertia: (0.2 * 0.4**3) / 12,
        isCustom: true,
        material: 'timber',
        sectionType: 'rectangular'
    }
    
    const timberLoads: SMBeamLoad[] = [
        { id: '33333333-3333-3333-3333-333333333334', type: 'UniformLoad', spanId: 1, startPosition: 0, endPosition: 4, uniformIntensity: 3, startIntensity: 3, endIntensity: 3, pointLoad: 0, pointLoadPosition: 0, normalForce: 0, normalForcePosition: 0, normalForceEccentricity: 0 },
        { id: '33333333-3333-3333-3333-333333333335', type: 'UniformLoad', spanId: 2, startPosition: 0, endPosition: 4, uniformIntensity: 3, startIntensity: 3, endIntensity: 3, pointLoad: 0, pointLoadPosition: 0, normalForce: 0, normalForcePosition: 0, normalForceEccentricity: 0 }
    ]
    timberModel.loadCases = [
        { id: '33333333-3333-3333-3333-333333333333', name: 'Timber Load', type: 'Variable', isActive: true, loads: timberLoads }
    ]

    const timberMaterial = {
        nationalAnnex: 'Standard',
        materialType: 'timber',
        elasticModulus: 13000e6, // GL30h
        density: 460,
        timberKind: 'glulam',
        timberClass: 'GL30h',
        timberGammaM: 1.25,
        timberGammaMSolid: 1.30,
        timberServiceClass: 2,
        timberLoadDuration: 'short-term'  // (short-term, SC2) → kmod 0.90 per EN 1995-1-1 Table 3.1
    }

    await runTest(testResults[2], timberModel, timberMaterial)

    setIsRunning(false)
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{t('admin.regression.title')}</h2>
        <button 
          className="btn btn-primary" 
          onClick={runAllTests}
          disabled={isRunning}
        >
          {isRunning ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              {t('admin.regression.runningTests')}
            </>
          ) : (
            <>
              <i className="bi bi-play-circle me-2"></i>
              {t('admin.regression.runAllTests')}
            </>
          )}
        </button>
      </div>

      <div className="row">
        {testResults.map(test => (
          <div key={test.id} className="col-12 mb-4">
            <div className={`card border-${test.status === 'success' ? 'success' : test.status === 'failed' ? 'danger' : 'secondary'}`}>
              <div className={`card-header bg-${test.status === 'success' ? 'success' : test.status === 'failed' ? 'danger' : 'light'} ${test.status === 'success' || test.status === 'failed' ? 'text-white' : ''}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{test.name}</h5>
                  <span className="badge bg-white text-dark">
                    {test.status.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="card-body bg-light text-dark font-monospace p-2" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
                {test.logs.length === 0 ? (
                  <span className="text-muted">{t('admin.regression.waitingToRun')}</span>
                ) : (
                  test.logs.map((log, i) => (
                    <div key={i} className="border-bottom border-light pb-1 mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default RegressionTestingPanel
